import type { Dapp } from '@src/dto';
import { isRight } from 'fp-ts/Either';
import { Coins, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { useDappDataOptional } from '../../contexts/DappDataContext';
import { useAsyncData } from '../../hooks/useAsyncData';
import { AuthStateType, useAuth } from '../../hooks/useAuth';
import { useDappReleases } from '../../hooks/useDappReleases';
import { useDappStoreStatus } from '../../hooks/useDappStoreStatus';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useTRPC } from '../../hooks/useTRPC';
import { captureAndReportError } from '../../utils/errorReporting';
import { getDappDetailsPath, getDappReviewsPath } from '../../utils/dappRoutes';
import { AppNftMintModal } from '../AppNftMintModal';
import { Button } from '../Button';
import { useOptionalDappContext } from '../DappLayout';
import { AppIcon, PortalCard, PortalPage, StatusPill } from '../portal';
import { useSidebarData } from '../sidebar/hooks/useSidebarData';
import { AppCollectionVerificationPrompt } from './AppCollectionVerificationPrompt';
import { DeleteDraftDappDialog } from './DeleteDraftDappDialog';
import {
  LatestReviewsCard,
  type LatestReviewsData,
} from './overview/LatestReviewsCard';
import {
  OverviewStats,
  type OverviewAnalytics,
} from './overview/OverviewStats';
import { OverviewSkeleton } from './overview/OverviewSkeleton';
import { OverviewSummaryCard } from './overview/OverviewSummaryCard';
import {
  countReviewsThisWeek,
  findLiveRelease,
  getDappStatusPill,
  getLiveVersionSummary,
  getNextReleaseSummary,
  getStoreListingSummary,
} from './overview/overviewSummary';

const REVIEWS_PAGE_SIZE = 10;

export const DashboardHome = () => {
  const dappRoute = useOptionalDappContext();
  const dappId = dappRoute?.dappId;
  const routeDapp = dappId
    ? { id: dappId, packageName: dappRoute.packageName }
    : null;
  const navigate = useNavigate();
  const trpc = useTRPC();
  const [authState] = useAuth();
  const { dappData } = useDappDataOptional();
  const { removeDapp } = useSidebarData();

  const {
    data: dapp,
    isLoading: isDappLoading,
    error: dappError,
    run: runDapp,
  } = useAsyncData<Dapp | null>({ initialData: null, initialLoading: true });
  const {
    releases,
    isLoading: releasesLoading,
    error: releasesError,
  } = useDappReleases(dappId);
  const storeStatus = useDappStoreStatus(dappId);

  const [reviews, setReviews] = useState<LatestReviewsData | null>(null);
  const [reviewsError, setReviewsError] = useState<string | null>(null);
  const [reviewsLoading, setReviewsLoading] = useState(true);
  const [analytics, setAnalytics] = useState<OverviewAnalytics | null>(null);

  const [showAppNftModal, setShowAppNftModal] = useState(false);
  const [isDeleteAppOpen, setIsDeleteAppOpen] = useState(false);
  const [isDeletingApp, setIsDeletingApp] = useState(false);
  const [isAppCollectionVerified, setIsAppCollectionVerified] = useState<
    boolean | null
  >(null);

  const displayName = dapp?.dappName || dappData?.dappName || null;
  usePageTitle(
    dappId && displayName
      ? `${displayName} - Overview | Publishing Portal`
      : 'Overview | Publishing Portal',
  );

  useEffect(() => {
    if (!dappId || authState.type !== AuthStateType.Authenticated) {
      return;
    }
    void runDapp(() => trpc.getDapp.query({ dappId }), {
      onSuccess: (result, helpers) => {
        if (isRight(result)) {
          helpers.setData(result.right);
        } else {
          helpers.setError(result.left.message || 'Failed to load app');
        }
      },
      onError: (err, helpers) => {
        captureAndReportError('Failed to fetch dapp details:', err);
        helpers.setError('Failed to load app');
      },
    });
  }, [dappId, authState.type, runDapp, trpc]);

  useEffect(() => {
    if (!dappId || authState.type !== AuthStateType.Authenticated) {
      return;
    }
    let cancelled = false;
    setReviewsLoading(true);
    trpc.getDappRatingsReviews
      .query({
        dappId,
        first: REVIEWS_PAGE_SIZE,
        page: 1,
        sortBy: 'newest',
      })
      .then((result) => {
        if (cancelled) return;
        if (!isRight(result)) {
          setReviewsError(result.left.message);
          return;
        }
        setReviewsError(null);
        setReviews({
          storeListingFound: result.right.storeListingFound,
          rating: result.right.summary.rating,
          reviewsByRating: result.right.summary.reviewsByRating,
          reviews: result.right.reviews,
        });
      })
      .catch((err) => {
        if (cancelled) return;
        captureAndReportError(err, {
          component: 'DashboardHome',
          operation: 'getDappRatingsReviews',
          dappId,
        });
        setReviewsError('Failed to load ratings and reviews');
      })
      .finally(() => {
        if (!cancelled) setReviewsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dappId, authState.type, trpc]);

  useEffect(() => {
    setAnalytics(null);
    if (!dappId || authState.type !== AuthStateType.Authenticated) {
      return;
    }
    let cancelled = false;
    // A Left (env-gated or no access) leaves the tiles on "Not available yet".
    void trpc.dappAnalytics.getDappAnalytics
      .query({ dappId, range: '7d' })
      .then((result) => {
        if (cancelled || !isRight(result)) return;
        setAnalytics({
          installs: result.right.totals.installs.value,
          uninstalls: result.right.totals.uninstalls.value,
          latestVersionPercent:
            result.right.activity.latestVersion7dPercent.value,
        });
      })
      .catch(() => {
        // Analytics is env-gated; missing data is expected.
      });
    return () => {
      cancelled = true;
    };
  }, [dappId, authState.type, trpc]);

  useEffect(() => {
    if (
      !dappId ||
      dapp?.canManageNfts !== true ||
      !dapp.nftMintAddress ||
      !dapp.publisherNftMintAddress
    ) {
      setIsAppCollectionVerified(null);
      return;
    }

    let isCancelled = false;
    trpc.checkOnChainAppCollectionStatus
      .query({ dappId })
      .then((result) => {
        if (isCancelled || result._tag === 'Left') return;
        setIsAppCollectionVerified(result.right.isVerifiedOnChain);
      })
      .catch((error) => {
        if (isCancelled) return;
        setIsAppCollectionVerified(null);
        captureAndReportError(error, {
          operation: 'dashboard.checkOnChainAppCollectionStatus',
          component: 'DashboardHome',
          dappId,
        });
      });

    return () => {
      isCancelled = true;
    };
  }, [
    dapp?.canManageNfts,
    dapp?.nftMintAddress,
    dapp?.publisherNftMintAddress,
    dappId,
    trpc,
  ]);

  const handleDeleteDraftApp = async () => {
    if (!dappId || dapp?.status !== 'Draft') return;

    setIsDeletingApp(true);
    try {
      const result = await trpc.deleteDraftDapp.mutate({ dappId });
      if (result._tag === 'Left') {
        toast.error(result.left.message);
        return;
      }

      removeDapp(dappId);
      toast.success(result.right.message);
      navigate('/dashboard', { replace: true });
    } catch (error) {
      captureAndReportError(error, {
        operation: 'deleteDraftDapp',
        component: 'DashboardHome',
        dappId,
      });
      toast.error('Failed to delete draft app');
    } finally {
      setIsDeletingApp(false);
    }
  };

  const statusContext = useMemo(
    () => ({
      publishedVersionCode: dapp?.publishedVersionCode,
      storeStatus: storeStatus.storeStatus,
    }),
    [dapp?.publishedVersionCode, storeStatus.storeStatus],
  );
  const liveRelease = useMemo(
    () => findLiveRelease(releases, dapp, statusContext),
    [releases, dapp, statusContext],
  );
  const liveVersionName =
    liveRelease?.versionName ??
    (statusContext.storeStatus === 'unpublished'
      ? null
      : (dapp?.publishedVersionName ?? null));
  const summaryColumns = useMemo(
    () =>
      [
        getLiveVersionSummary(releases, dapp, statusContext),
        getNextReleaseSummary(releases, statusContext),
        getStoreListingSummary(dapp?.storeListingDraft ?? null, dapp),
      ] as const,
    [releases, dapp, statusContext],
  );
  const totalReviews = reviews?.storeListingFound
    ? reviews.reviewsByRating.reduce((sum, count) => sum + count, 0)
    : null;
  const reviewsThisWeek = reviews
    ? countReviewsThisWeek(reviews.reviews, REVIEWS_PAGE_SIZE)
    : null;

  const statusPill = dapp
    ? getDappStatusPill(dapp, liveRelease != null, statusContext.storeStatus)
    : null;
  const subline =
    storeStatus.categories.length > 0
      ? storeStatus.categories.join(', ')
      : dapp?.subtitle || dapp?.packageName || dappRoute?.packageName || '';
  const mintAppData =
    dapp && dapp.walletAddress && dapp.dappIconUrl
      ? {
          id: dapp.id,
          name: dapp.dappName,
          iconUrl: dapp.dappIconUrl ?? undefined,
          website: dapp.appWebsite ?? undefined,
          androidPackage: dapp.packageName || 'com.example.app',
          publisherAddress: dapp.walletAddress,
        }
      : null;

  // First paint: one coherent skeleton until the dapp lands; slower sections
  // (releases, reviews) keep their own matching skeletons after that.
  const isDappPending = Boolean(dappId) && isDappLoading && !dapp;
  const title = displayName ?? dappRoute?.packageName ?? 'Overview';

  return (
    <PortalPage>
      {isDappPending ? (
        <OverviewSkeleton />
      ) : (
        <div className="motion-enter flex flex-col gap-10">
          <header className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex min-w-0 flex-col items-start gap-4 sm:flex-row sm:items-center sm:gap-6">
              <AppIcon
                iconUrl={dapp?.dappIconUrl}
                thumbnailUrl={dapp?.dappIconThumbnailUrl}
                name={title}
                size={96}
              />
              <div className="flex min-w-0 flex-col gap-2">
                <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2">
                  <div
                    role="heading"
                    aria-level={1}
                    className="min-w-0 break-words text-4xl font-medium leading-10 text-white sm:truncate"
                    title={title}
                  >
                    {title}
                  </div>
                  {statusPill ? (
                    <StatusPill tone={statusPill.tone} size="md">
                      {statusPill.label}
                    </StatusPill>
                  ) : null}
                </div>
                {subline ? (
                  <span className="truncate text-sm text-white" title={subline}>
                    {subline}
                  </span>
                ) : null}
              </div>
            </div>
            {dapp ? (
              <div className="flex w-full flex-col gap-3 sm:w-auto sm:shrink-0 sm:flex-row sm:flex-wrap sm:items-center">
                {dapp.status === 'Draft' && dapp.canManageDapps ? (
                  <Button
                    variant="dangerOutline"
                    className="w-full sm:w-auto"
                    onClick={() => setIsDeleteAppOpen(true)}
                  >
                    <Trash2 className="size-4" />
                    Delete draft
                  </Button>
                ) : null}
                {mintAppData && !dapp.nftMintAddress ? (
                  <Button
                    variant="outlineWhite"
                    className="w-full sm:w-auto"
                    onClick={() => setShowAppNftModal(true)}
                  >
                    <Coins className="size-4" />
                    Mint App
                  </Button>
                ) : null}
              </div>
            ) : null}
          </header>

          {dappError ? (
            <PortalCard className="p-6 text-sm text-rose-400">
              {dappError}
            </PortalCard>
          ) : null}

          <AppCollectionVerificationPrompt
            appData={dapp}
            isAppCollectionVerified={isAppCollectionVerified}
            onVerificationSuccess={() => setIsAppCollectionVerified(true)}
          />

          {dappError ? null : releasesError ? (
            <PortalCard className="p-6 text-sm text-rose-400">
              {releasesError}
            </PortalCard>
          ) : (
            <OverviewSummaryCard
              columns={[...summaryColumns]}
              storeListingPath={
                routeDapp ? getDappDetailsPath(routeDapp) : null
              }
              isLoading={releasesLoading}
            />
          )}

          <OverviewStats
            liveVersionName={liveVersionName}
            analytics={analytics}
            totalReviews={totalReviews}
            reviewsThisWeek={reviewsThisWeek}
            isLoading={reviewsLoading && !reviews}
          />

          <LatestReviewsCard
            data={reviews}
            isLoading={reviewsLoading && !reviews}
            error={reviewsError}
            reviewsPath={routeDapp ? getDappReviewsPath(routeDapp) : null}
          />
        </div>
      )}

      {mintAppData ? (
        <AppNftMintModal
          isOpen={showAppNftModal}
          onClose={() => setShowAppNftModal(false)}
          onSuccess={() => {
            setShowAppNftModal(false);
            toast.success('App NFT minted successfully!');
            setTimeout(() => {
              window.location.reload();
            }, 1000);
          }}
          appData={mintAppData}
        />
      ) : null}

      <DeleteDraftDappDialog
        dappName={dapp?.dappName ?? ''}
        isOpen={isDeleteAppOpen}
        isDeleting={isDeletingApp}
        onClose={() => setIsDeleteAppOpen(false)}
        onDelete={handleDeleteDraftApp}
      />
    </PortalPage>
  );
};
